import React, { FC, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useMediaQuery } from '@mui/material';
import { DndContext, PointerSensor, useSensor, useSensors, DragEndEvent, closestCenter } from '@dnd-kit/core';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { useAppDispatch, useAppSelector } from '../../../hooks/redux';
import { useCustomSnackbar } from '../../../hooks/useCustomEnqueueSnackbar';
import { CustomTab } from '../../commons/CustomTab';
import { TabPanel } from '../../commons/CustomTab/TabPanel';
import { multiAccountTabs } from './MultiAccountTabs';
import { CustomSkeletonElement } from '../../commons/CustomSkeletonElement';
import { modalSlice } from '../../../store/reducers/ModalSlice';
import { userInfoApi } from '../../../services/apiServices/user/userInfo';
import { ModalVariants, LocalStorageItems, GlobalRouteNames } from '../../../models/Common';
import { IMultiAccount } from '../../../models/UserInfo';
import { AdminTopBar } from './AdminTopBar';
import { PrioritiesTable } from './PrioritiesTable';
import { UserAccount } from './UserAccount';
import { MobileTableBody } from './MobileTableBody';
import { NoAccountsFound } from './NoAccountsFound';
import { NoConnectedAccounts } from './NoConnectedAccounts';
import { TableBody } from './TableBody';
import { TableHead } from './TableHead';
import { getErrorMessage } from '../../../utils';

export const MultiAccount: FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { openModal } = modalSlice.actions;
  const [tabValue, setTabValue] = useState<number>(0);
  const [searchValue, setSearchValue] = useState<string>('');
  const [visibleAction, setVisibleAction] = useState<number>(Infinity);
  const [priorityList, setPriorityList] = useState<IMultiAccount[]>([]);
  const { customEnqueueSnackbar } = useCustomSnackbar();
  const isMobile = useMediaQuery('(max-width:1279px)');
  const { selectedLanguage } = useAppSelector((state) => state.langReducer);
  const { data, isLoading, fulfilledTimeStamp, isError, error } = userInfoApi.useGetMultiaccountMembersQuery(
    selectedLanguage,
    {
      refetchOnMountOrArgChange: true,
    },
  );
  const [changePriority, { isLoading: isChangingPriority }] = userInfoApi.useChangeMembersPrioritiesMutation();

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 1 } }));

  useEffect(() => {
    if (!data?.List) return;
    setPriorityList(data.List);
  }, [fulfilledTimeStamp]);

  const handlePrioritiesDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const adminAcc = priorityList.find((x) => x.Role === 'admin');
    const sortableAcc = priorityList.filter((x) => x.Role !== 'admin');
    const oldIndex = sortableAcc.findIndex((i) => i.id === active.id);
    const newIndex = sortableAcc.findIndex((i) => i.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    const newSortable = arrayMove(sortableAcc, oldIndex, newIndex);
    const newList = adminAcc ? [adminAcc, ...newSortable] : newSortable;

    setPriorityList(newList);

    const userIds = newList.map((item) => item.UserID);

    try {
      await changePriority({ userIds }).unwrap();
      customEnqueueSnackbar(t('multiAccount.prioritiesChanged'), 'success');
    } catch (err) {
      const message = getErrorMessage(err);
      customEnqueueSnackbar(message, 'error');
    }
  };

  useEffect(() => {
    if (isError && error) {
      const message = getErrorMessage(error);
      customEnqueueSnackbar(message, 'error');
    }
  }, [isError, error]);

  const handleSetTabValue = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setSearchValue('');
  };

  const handleToggleOptions = (id: number) => {
    setVisibleAction((prev) => (prev !== id ? id : Infinity));
  };

  const handleViewServices = (item: IMultiAccount) => {
    setVisibleAction(Infinity);
    if (item.Role === 'member') {
      localStorage.setItem(LocalStorageItems.ControlledAccount, JSON.stringify(item));
    } else {
      localStorage.removeItem(LocalStorageItems.ControlledAccount);
    }
    dispatch(userInfoApi.util.invalidateTags(['Accounts']));
    history.push(GlobalRouteNames.HomePage);
  };

  const handleDeleteFromAdmin = (id: number) => {
    setVisibleAction(Infinity);
    dispatch(
      openModal({
        modalVariants: ModalVariants.DeleteAccount,
        content: {
          accountId: String(id),
        },
      }),
    );
  };

  const filterAccounts = (item: IMultiAccount) => {
    const searchStr = searchValue.trim().toLowerCase();
    return (
      item.UserID.toString().includes(searchStr) ||
      item.User?.Name.toLowerCase().includes(searchStr) ||
      item.User?.Address.toLowerCase().includes(searchStr)
    );
  };

  const getFilteredAccounts = (item: IMultiAccount[]) => {
    return item.filter((listItem) => filterAccounts(listItem));
  };

  return (
    <div className="multi-account">
      {(() => {
        if (isLoading) {
          return (
            !isMobile && (
              <CustomSkeletonElement
                skeletonContainerClassName="skeleton-container"
                skeletonChildrenClassName="skeleton-children skeleton"
                childrenCount={1}
              />
            )
          );
        }
        if (!isLoading && data && data.Account) {
          return (
            <>
              {data.Account.Role === 'admin' ? (
                <div className="multi-account__admin">
                  <CustomTab
                    tabValue={tabValue}
                    setTabValue={handleSetTabValue}
                    orientation="horizontal"
                    tabs={multiAccountTabs.map((el) => {
                      return { label: t(el.label), id: el.id };
                    })}
                  />
                  <TabPanel value={tabValue} index={0}>
                    <AdminTopBar value={searchValue} setValue={setSearchValue} />
                    <div className="multi-account__admin__table">
                      {getFilteredAccounts(data.List).length ? (
                        <>
                          {!isMobile && <TableHead />}
                          {getFilteredAccounts(data.List).map((acc) => {
                            return !isMobile ? (
                              <TableBody
                                key={acc.id}
                                item={acc}
                                visibleAction={visibleAction}
                                handleDeleteFromAdmin={handleDeleteFromAdmin}
                                handleToggleOptions={handleToggleOptions}
                                handleViewServices={handleViewServices}
                              />
                            ) : (
                              <MobileTableBody
                                key={acc.id}
                                item={acc}
                                handleToggleOptions={handleToggleOptions}
                                handleDeleteFromAdmin={handleDeleteFromAdmin}
                                handleViewServices={handleViewServices}
                                visibleAction={visibleAction}
                              />
                            );
                          })}
                        </>
                      ) : (
                        <NoAccountsFound />
                      )}
                    </div>
                  </TabPanel>
                  <TabPanel value={tabValue} index={1}>
                    <div className="multi-account__admin__table" style={{ display: 'flex' }}>
                      {priorityList.find((x) => x.Role === 'admin') ? (
                        <PrioritiesTable
                          key={priorityList.find((x) => x.Role === 'admin')?.id}
                          item={priorityList.find((x) => x.Role === 'admin') as IMultiAccount}
                          isStatic
                          isLoading={isChangingPriority}
                        />
                      ) : null}
                      {priorityList.filter((x) => x.Role !== 'admin').length ? (
                        <DndContext
                          sensors={sensors}
                          collisionDetection={closestCenter}
                          onDragEnd={handlePrioritiesDragEnd}
                          modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                        >
                          <SortableContext
                            items={priorityList.filter((x) => x.Role !== 'admin').map((i) => i.id)}
                            strategy={verticalListSortingStrategy}
                          >
                            {priorityList
                              .filter((x) => x.Role !== 'admin')
                              .map((acc) => (
                                <PrioritiesTable key={acc.id} item={acc} isLoading={isChangingPriority} />
                              ))}
                          </SortableContext>
                        </DndContext>
                      ) : null}
                    </div>
                  </TabPanel>
                </div>
              ) : (
                <UserAccount
                  isVerified={data.Account.Status === 'verified'}
                  userName={data.List.find((acc) => acc.Role === 'admin')?.User?.Name}
                  userId={data.Account?.UserID}
                />
              )}
            </>
          );
        }
        if (isError) {
          return <p className="multi-account__is-error">{t('paymentHistory.errorOccurred')}</p>;
        }
        return <NoConnectedAccounts />;
      })()}
    </div>
  );
};
